import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Input,
  Popconfirm,
  Spin,
  Table,
  Typography,
} from 'antd';
import moment from 'moment';
import { BANNED_EMAIL_DOMAINS } from 'api/bannedEmailDomains/queries';
import {
  ADD_BANNED_EMAIL_DOMAIN,
  REMOVE_BANNED_EMAIL_DOMAIN,
} from 'api/bannedEmailDomains/mutations';
import {
  BannedEmailDomains,
  BannedEmailDomains_bannedEmailDomains,
} from 'api/bannedEmailDomains/types/BannedEmailDomains';
import {
  AddBannedEmailDomain,
  AddBannedEmailDomainVariables,
} from 'api/bannedEmailDomains/types/AddBannedEmailDomain';
import {
  RemoveBannedEmailDomain,
  RemoveBannedEmailDomainVariables,
} from 'api/bannedEmailDomains/types/RemoveBannedEmailDomain';
import { getProperErrorMessage } from 'helpers/errors';
import {
  successNotification,
  errorNotification,
  warningNotification,
} from 'ui/Notification';
import styles from './PersonalEmailDomainsSettings.module.scss';

const { Text, Title } = Typography;

const PersonalEmailDomainsSettings = (): JSX.Element => {
  const [domain, setDomain] = useState('');
  const [reason, setReason] = useState('');

  const { data, loading, error, refetch } = useQuery<BannedEmailDomains>(
    BANNED_EMAIL_DOMAINS,
    { fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true }
  );
  const [addDomain, { loading: adding }] = useMutation<
    AddBannedEmailDomain,
    AddBannedEmailDomainVariables
  >(ADD_BANNED_EMAIL_DOMAIN);
  const [removeDomain] = useMutation<
    RemoveBannedEmailDomain,
    RemoveBannedEmailDomainVariables
  >(REMOVE_BANNED_EMAIL_DOMAIN);

  const refetchList = (): Promise<unknown> => refetch().catch(() => undefined);

  const handleAdd = async (): Promise<void> => {
    try {
      await addDomain({
        variables: { input: { domain, reason: reason.trim() || null } },
      });
      successNotification('Domain added');
      setDomain('');
      setReason('');
      await refetchList();
    } catch (err: any) {
      errorNotification(
        getProperErrorMessage(err, err?.message || 'Failed to add the domain')
      );
    }
  };

  const handleRemove = async (value: string): Promise<void> => {
    try {
      const result = await removeDomain({
        variables: { input: { domain: value } },
      });
      if (result.data?.removeBannedEmailDomain === false) {
        warningNotification('Domain was already removed');
      } else {
        successNotification('Domain removed');
      }
      await refetchList();
    } catch (err: any) {
      errorNotification(
        getProperErrorMessage(
          err,
          err?.message || 'Failed to remove the domain'
        )
      );
    }
  };

  const columns = [
    { title: 'Domain', dataIndex: 'domain', key: 'domain' },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason: string | null) => reason || '-',
    },
    {
      title: 'Added',
      dataIndex: 'addedAt',
      key: 'addedAt',
      render: (addedAt: string) => moment(addedAt).format('MMM D, YYYY'),
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, record: BannedEmailDomains_bannedEmailDomains) => (
        <Popconfirm
          title={`Remove ${record.domain}?`}
          onConfirm={() => handleRemove(record.domain)}
        >
          <Button danger type="link" aria-label={`Remove ${record.domain}`}>
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load the personal email domains"
        description={error.message}
      />
    );
  }

  if (loading && !data) {
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Title level={4}>Personal email domains</Title>
      <Text type="secondary">
        People whose email is on one of these domains can&apos;t sign up as a
        brand or upgrade a fan account to a brand account.
      </Text>

      <div className={styles.addRow}>
        <Input
          placeholder="example.com"
          aria-label="Domain"
          value={domain}
          onChange={(event) => setDomain(event.target.value)}
          onPressEnter={() => !adding && domain.trim() && handleAdd()}
        />
        <Input
          placeholder="Reason (optional)"
          aria-label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <Button
          type="primary"
          loading={adding}
          disabled={!domain.trim()}
          onClick={handleAdd}
        >
          Add domain
        </Button>
      </div>

      <Table
        rowKey="domain"
        size="small"
        columns={columns}
        dataSource={data?.bannedEmailDomains ?? []}
        pagination={{ pageSize: 50 }}
      />
    </div>
  );
};

export default PersonalEmailDomainsSettings;
